import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { generateCompletionCertificate, getCourseById, getPurchasedCourseBySlug } from "../../../../redux/features/course/courseService";
import styles from "./index.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileVideo } from "@fortawesome/free-regular-svg-icons";
import {
  faCalendar,
  faChevronRight,
  faClock,
  faDownload,
  faFilePdf,
  faMoneyBill,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import { selectUserDetails } from "../../../../redux/features/auth/authSlice";
import Skeleton from "react-loading-skeleton";
import { faLinkedin, faSquareFacebook } from "@fortawesome/free-brands-svg-icons";
import BeatLoader from "react-spinners/BeatLoader";
import Background from "../../../../assets/images/brand/sh_certificate_bg.jpeg";
import { showToast } from "../../../../components/ToastMsg";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const MyCourse = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  const { isCourseLoading, courseDetails, lessons } = useSelector(
    (state) => state.course.course
  );

  const { isPurchasedCourseLoading, purchasedCourse } = useSelector(
    (state) => state.course.purchasedCourse
  );

  const { isLoggedIn } = useSelector(selectUserDetails);

  const [expandedLessons, setExpandedLessons] = useState({});

  useEffect(() => {
    dispatch(getCourseById({ slug }));
    dispatch(getPurchasedCourseBySlug({ slug }));
  }, [dispatch, slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const toggleDetails = (id) => {
    setExpandedLessons((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const [expandedCertificate, setCertificateExpand] = useState(false);

  const toggleCertificate = () => {
    setCertificateExpand((prev) => (!prev));
  };

  const openVideoPage = (videoSlug) => {
    navigate(`/my-courses/${courseDetails?.slug}/${videoSlug}`);
  };

  const canvasRef = useRef(null);

  const formatDate = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [isGenerating, setGenerating] = useState(false);

  const generateCertificate = async () => {
    try {
      setGenerating(true);

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error("Canvas not found");
      }

      const ctx = canvas.getContext("2d");

      canvas.width = 2000;
      canvas.height = 1400;

      // Wait for fonts to be ready
      await document.fonts.ready;

      // 🔥 Helper to load image safely
      const loadImage = (src) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = (e) => {
            reject(new Error("Failed to load image"));
          };
          img.src = src;
        });

      // 🔥 Load background image
      const bg = await loadImage('/SH_certificate_final.png');

      // Clear and draw background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

      // ================= TEXT DRAWING =================

      // Title
      ctx.font = "600 60px Open Sans";
      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      ctx.fillText(
        purchasedCourse?.[0]?.course?.title || "",
        canvas.width / 2,
        400
      );

      // Name
      ctx.font = "bold 50px Open Sans";
      ctx.fillText(
        purchasedCourse?.[0]?.user?.full_name || "",
        canvas.width / 2,
        900
      );

      // Date
      ctx.font = "34px Open Sans";
      ctx.textAlign = "right";
      ctx.fillText(
        formatDate(),
        canvas.width - 510,
        canvas.height - 310
      );

      // ================= EXPORT =================

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      if (!blob) {
        throw new Error("Canvas is empty");
      }

      const file = new File([blob], "certificate.png", {
        type: "image/png",
      });

      // ================= OPTIONAL API =================

      const formData = new FormData();
      formData.append("course_id", courseDetails?.id);
      formData.append("completion_status", "COMPLETED");
      formData.append("image", file);

      await dispatch(generateCompletionCertificate(formData)).unwrap();

      dispatch(getCourseById({ slug }));
      dispatch(getPurchasedCourseBySlug({ slug }));

      setGenerating(false);
    } catch (error) {
      showToast('error', 'Failed to generate the certificate. Please try again later.');
      setGenerating(false);
    }
  };

  const downloadImage = async () => {
    const imageUrl = purchasedCourse[0]?.completion_certificate_url;
    if (!imageUrl) return;
    window.open(imageUrl, "_blank");
    // try {
    //   const response = await fetch(imageUrl);
    //   const blob = await response.blob();
    //   const url = window.URL.createObjectURL(blob);

    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.download = `${purchasedCourse[0]?.user?.full_name}_${purchasedCourse[0]?.course?.title}_certificate.png`; // The name for the saved file
    //   document.body.appendChild(link);
    //   link.click();

    //   // Cleanup
    //   document.body.removeChild(link);
    //   window.URL.revokeObjectURL(url);
    // } catch (error) {
    //   console.error("Download failed:", error);
    // }
  };

  // const shareCertificate = async () => {
  //   const imageUrl = purchasedCourse[0]?.completion_certificate_url;
  //   if (!imageUrl) return;

  //   try {
  //     const response = await fetch(imageUrl);
  //     const blob = await response.blob();
  //     const file = new File([blob], "certificate.png", { type: "image/png" });

  //     if (navigator.canShare && navigator.canShare({ files: [file] })) {
  //       await navigator.share({
  //         files: [file],
  //         title: "My Course Certificate",
  //         text: "I just completed this course on StudyHub!",
  //       });
  //     } else {
  //       // Fallback: Copy link to clipboard or open LinkedIn URL
  //       alert("Sharing not supported on this browser. Link copied!");
  //       navigator.clipboard.writeText(imageUrl);
  //     }
  //   } catch (error) {
  //     console.error("Sharing failed", error);
  //   }
  // };

  const shareToFacebook = (certificate) => {
    const { completion_certificate_url, user, course } = certificate;

    if (!completion_certificate_url) {
      alert("Certificate URL not found.");
      return;
    }

    // Create dynamic share URL with query params
    const shareUrl = `${window.location.origin}/share-certificate.html?userName=${encodeURIComponent(
      user.full_name
    )}&courseName=${encodeURIComponent(course.title)}&imageUrl=${encodeURIComponent(
      completion_certificate_url
    )}`;

    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl
    )}`;

    window.open(fbShareUrl, "facebook-share-dialog", "width=626,height=436");
  };

  const addToLinkedIn = () => {
    const baseUrl = "https://www.linkedin.com/profile/add?";
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: purchasedCourse[0]?.course?.title,
      organizationName: "Studyhub Academy",
      issueYear: new Date().getFullYear(),
      issueMonth: new Date().getMonth() + 1,
      certUrl: purchasedCourse[0]?.completion_certificate_url, // Link to the image/doc
    });

    window.open(`${baseUrl}${params.toString()}`, "_blank");
  };

  const shareToLinkedInFeed = () => {
    const certificateUrl = purchasedCourse[0]?.completion_certificate_url;

    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certificateUrl)}`;

    window.open(linkedInUrl, "_blank", "width=600,height=600");
  };

  const openMentor = (id) => {
    window.open(
      `${window.location.origin}/mentors/${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div>
      {isCourseLoading ? (
        <>
          <section className={styles.A}>
            <div className="main-container">
              <div className={`container-content ${styles.headerContent}`}>
                <p className={styles.title}><Skeleton width={600} /></p>
                <Skeleton width={200} />
                <p className={styles.auther}>
                  <Skeleton width={400} />
                </p>
                <div className={styles.detailsRow}>
                  <p className={styles.detailsP}>
                    <Skeleton width={200} />
                  </p>
                  <p className={styles.detailsP}>
                    <Skeleton width={200} />
                  </p>
                </div>
              </div>
            </div>
          </section>
          <div className="main-container">
            <div className="container-content">
              <div className={styles.layout}>
                <div className={styles.C}>
                  <p className={styles.courseContent}><Skeleton /></p>
                  <div className={styles.courseContentDetails}>
                    <span><Skeleton width={50} /></span>
                    <span><Skeleton width={50} /></span>
                  </div>
                  <div className={styles.courseLessonsSec} style={{ border: 'none' }}>
                    {Array.from({ length: 5 }).map((_, rowIndex) => (
                      <div className={styles.titles} key={rowIndex}>
                        <Skeleton className={styles.lessonTitle} />
                      </div>
                    ))}
                  </div>
                </div>
                <aside className={styles.B}>
                  <Skeleton
                    src={courseDetails?.image_url} height={200} />
                  <p className={styles.sidebarDesc}>
                    <Skeleton count={5} />
                  </p>
                  <div className={styles.mobileDetails}>
                    <Skeleton count={5} />
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <section className={styles.A}>
            <div className="main-container">
              <div className={`container-content ${styles.headerContent}`}>
                <p className={styles.title}>{courseDetails?.title}</p>

                <p className={styles.imageLabel}>
                  {courseDetails?.course_category?.name}
                </p>

                {/* <p className={styles.infoTitle}>
            {courseDetails?.description}
          </p> */}

                <p className={styles.auther}>
                  Created by <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openMentor(courseDetails?.user?.id)}>{courseDetails?.user?.full_name}</span>
                </p>
                {/* <div className={styles.detailsRow}>
                  <p className={styles.detailsP}>
                    <FontAwesomeIcon icon={faUser} />
                    Enrolls {courseDetails?.download}
                  </p>
                  <p className={styles.detailsP}>
                    <FontAwesomeIcon icon={faMoneyBill} />
                    Rs. {courseDetails?.discount_price > 0 ? formatNumber(courseDetails?.discount_price) : formatNumber(courseDetails?.price)}
                    <span style={{ textDecoration: 'line-through', color: '#5B5A5A' }}>Rs. {courseDetails?.discount_price > 0 && courseDetails?.price?.toFixed(2)}</span>
                  </p>
                </div> */}
              </div>
            </div>
          </section>
          <div className="main-container">
            <div className="container-content">
              <div className={styles.layout}>

                {/* LEFT - C */}
                <div className={styles.C}>
                  <p className={styles.courseContent}>Course Content</p>
                  <div className={styles.courseContentDetails}>
                    <span>{courseDetails?.sub_titles.length} Sections</span>
                    <span>{courseDetails?.duration} total length</span>
                    <span>Last update {courseDetails?.last_update}</span>
                  </div>
                  <div className={styles.courseLessonsSec}>
                    {[...(lessons || [])]
                      .sort((a, b) => a.id - b.id)
                      .map((lesson) => (
                        <div className={styles.titles} key={lesson.id}>

                          {/* LESSON HEADER */}
                          <div
                            className={styles.lessonTitle}
                            onClick={() => toggleDetails(lesson.id)}
                          >
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              style={{
                                transform: expandedLessons[lesson.id]
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                                transition: "0.3s",
                              }}
                            />
                            {lesson.title}
                          </div>

                          {/* LESSON CONTENT */}
                          {expandedLessons[lesson.id] &&
                            lesson.videos?.map((video) => (
                              <div className={styles.showBox} key={video.id}>

                                <div className={styles.descRow}>
                                  <FontAwesomeIcon
                                    icon={faFileVideo}
                                    className={styles.icon}
                                  />
                                  <div
                                    className={styles.name}
                                    onClick={() => openVideoPage(video.slug)}
                                  >
                                    {video.title}
                                  </div>
                                </div>
                                {/* {video.doc_title && (
                                  <div className={styles.descRow}>
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    <div className={styles.name}>
                                      {video.doc_title}
                                    </div>
                                  </div>
                                )} */}

                              </div>
                            ))}
                        </div>
                      ))}
                    <div className={styles.titles}>
                      <div
                        className={styles.lessonTitle}
                        onClick={() => toggleCertificate()}
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          style={{
                            transform: expandedCertificate
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            transition: "0.3s",
                          }}
                        />
                        Completion Certificate
                      </div>
                      {expandedCertificate &&
                        <div className={styles.showBox}>
                          <div className={styles.descRow}>
                            {purchasedCourse[0]?.completion_status ?
                              // <PDFViewer className={styles.certificatePdf}>
                              //   <CompletionCertificate data={['lll', 'qrDataUrl']} />
                              // </PDFViewer>

                              <img src={purchasedCourse[0]?.completion_certificate_url} className={styles.certificatePdf} />
                              : <button className={`primary-btn ${styles.certificateBtn}`} onClick={generateCertificate}>{isGenerating ? <BeatLoader color="#FFFFFF" /> : `Generate Your Certificate`}</button>
                            }
                            <div className={styles.descRow}>
                              <canvas ref={canvasRef} className={styles.certificatePdf} style={{ marginTop: '-2000px' }} />
                            </div>
                          </div>
                          {purchasedCourse[0]?.completion_status &&
                            <div className={styles.shareRow}>
                              {/* <button onClick={() => shareToFacebook(purchasedCourse[0])} className={`primary-btn ${styles.shareBtn}`}><FontAwesomeIcon icon={faSquareFacebook} /> Share</button> */}
                              <button onClick={addToLinkedIn} className={`primary-btn ${styles.shareBtn}`}>Add to <FontAwesomeIcon icon={faLinkedin} /> Profile</button>
                              <button className={styles.shareBtn} onClick={shareToLinkedInFeed}> Post to <FontAwesomeIcon icon={faLinkedin} /> Feed </button>
                              <button onClick={downloadImage} className={`primary-btn ${styles.shareBtn}`}><FontAwesomeIcon icon={faDownload} /> Certificate</button>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>

                {/* RIGHT - B */}
                <aside className={styles.B}>
                  <img
                    src={courseDetails?.image_url}
                    className={styles.thumbImg}
                    alt="course"
                  />
                  <p className={styles.sidebarDesc}>
                    {courseDetails?.description}
                  </p>
                  <div className={styles.mobileDetails}>
                    <p className={styles.titleMobile}>{courseDetails?.title}</p>

                    <p className={styles.imageLabel}>
                      {courseDetails?.course_category?.name}
                    </p>
                    <p className={styles.sidebarDescMobile}>
                      {courseDetails?.description}
                    </p>
                    <p className={styles.autherMobile}>
                      Created by {courseDetails?.user?.full_name}
                    </p>
                    <div className={styles.detailsRowMobile}>
                      <p className={styles.detailsP}>
                        <FontAwesomeIcon icon={faUser} />
                        Enrolls {courseDetails?.download}
                      </p>
                      <p className={styles.detailsP}>
                        <FontAwesomeIcon icon={faMoneyBill} />
                        Rs. {courseDetails?.discount_price > 0 ? formatNumber(courseDetails?.discount_price) : formatNumber(courseDetails?.price)}
                        <span style={{ textDecoration: 'line-through', color: '#5B5A5A' }}>Rs. {courseDetails?.discount_price > 0 && courseDetails?.price?.toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default MyCourse;